import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { CircularProgress, Box } from "@mui/material";
import { HelmetProvider } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import ErrorBoundary from "../ErrorBoundary/ErrorBoundary";
import { Actions as authActions } from "../../redux/auth";

// Lazy load components
const LoginPage = lazy(() => import("../LoginPage/LoginPage"));
const NotFoundPage = lazy(() => import("../NotFoundPage/NotFoundPage"));
const Layout = lazy(() => import("../Layout/Layout"));
const Stream = lazy(() => import("../Stream/Stream"));
const StreamMultiroom = lazy(() => import("../StreamMultiroom/StreamMultiroom"));
const StreamCustom = lazy(() => import("../StreamCustom/StreamCustom"));
const Admin = lazy(() => import("../Admin/Admin"));
const ProtectedRoute = lazy(() => import("../ProtectedRoute/ProtectedRoute"));

const LoadingFallback = () => (
  <Box 
    display="flex" 
    justifyContent="center" 
    alignItems="center" 
    height="100vh"
  >
    <CircularProgress size={60} />
  </Box>
);

// AppContent component to use hooks
const AppContent = () => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const token = localStorage.getItem('session');
  const [hasInitialized, setHasInitialized] = useState(false);

  // Debug logging
  console.log('App Debug:', {
    user,
    isAuthenticated,
    token: !!token,
    hasInitialized,
    userKeys: user ? Object.keys(user) : [],
    localStorageSession: !!localStorage.getItem('session')
  });

  useEffect(() => {
    // Only fetch once on initial load if we have a token but no user data
    if (token && !hasInitialized && (!user || Object.keys(user).length === 0 || !user.streams)) {
      console.log('Fetching user data from token');
      setHasInitialized(true);
      dispatch(authActions.fetchStreamFromToken(token));
    }
  }, [dispatch, token, hasInitialized, user]); // Added user back to dependencies

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={
              isAuthenticated ? <Navigate to="/stream?room=1" replace /> : <LoginPage />
            } />
            <Route path="/login" element={
              isAuthenticated ? <Navigate to="/stream?room=1" replace /> : <LoginPage />
            } />
            <Route
              path="/multistream"
              element={
                <Layout>
                  <ProtectedRoute component={StreamMultiroom} />
                </Layout>
              }
            />
            <Route
              path="/stream"
              element={
                <Layout>
                  <ProtectedRoute component={Stream} />
                </Layout>
              }
            />
            <Route
              path="/customstream"
              element={
                <Layout>
                  <ProtectedRoute component={StreamCustom} />
                </Layout>
              }
            />
            <Route
              path="/admin"
              element={
                <Layout>
                  <ProtectedRoute component={Admin} adminOnly />
                </Layout>
              }
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default function App() {
  return (
    <HelmetProvider>
      <AppContent />
    </HelmetProvider>
  );
}
